import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, ListGroup, Row } from 'react-bootstrap';
import { GruposService } from '../../../../service/grupos.service';
import { Grupo } from '../../../../service/types/cursos.types';
import { SimpleUser } from './AsignacionManager';

interface GrupoManagerProps {
  fetchUsers: () => Promise<SimpleUser[]>;
}

export default function GrupoManager({ fetchUsers }: GrupoManagerProps) {
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [users, setUsers] = useState<SimpleUser[]>([]);
  const [nombreNuevoGrupo, setNombreNuevoGrupo] = useState('');
  const [selectedGrupo, setSelectedGrupo] = useState<Grupo | null>(null);
  const [selectedRut, setSelectedRut] = useState('');

  const loadGrupos = async () => {
    const data = await GruposService.findAll();
    setGrupos(data);
  };

  useEffect(() => {
    loadGrupos();
    fetchUsers().then(setUsers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    if (!nombreNuevoGrupo.trim()) {
      alert('Ingrese un nombre para el grupo');
      return;
    }
    await GruposService.create({ nombre: nombreNuevoGrupo });
    setNombreNuevoGrupo('');
    await loadGrupos();
  };

  const handleDelete = async (grupo: Grupo) => {
    if (!window.confirm(`¿Eliminar el grupo "${grupo.nombre}"?`)) return;
    await GruposService.remove(grupo.id);
    if (selectedGrupo?.id === grupo.id) setSelectedGrupo(null);
    await loadGrupos();
  };

  const openGrupo = async (grupo: Grupo) => {
    const detalle = await GruposService.findOne(grupo.id);
    setSelectedGrupo(detalle);
  };

  const handleAddUsuario = async () => {
    if (!selectedGrupo) return;
    if (!selectedRut) {
      alert('Seleccione un usuario');
      return;
    }
    const grupo = await GruposService.addUsuario(selectedGrupo.id, selectedRut);
    setSelectedGrupo(grupo);
    setSelectedRut('');
    await loadGrupos();
  };

  const handleRemoveUsuario = async (rut: string) => {
    if (!selectedGrupo) return;
    const grupo = await GruposService.removeUsuario(selectedGrupo.id, rut);
    setSelectedGrupo(grupo);
    await loadGrupos();
  };

  return (
    <Card className="mb-3">
      <Card.Header>Grupos</Card.Header>
      <Card.Body>
        <Row>
          <Col md={5}>
            <div className="d-flex gap-2 mb-3">
              <Form.Control
                value={nombreNuevoGrupo}
                onChange={(e) => setNombreNuevoGrupo(e.target.value)}
                placeholder="Nombre del nuevo grupo"
              />
              <Button variant="success" onClick={handleCreate}>
                Crear
              </Button>
            </div>
            <ListGroup>
              {grupos.map((grupo) => (
                <ListGroup.Item
                  key={grupo.id}
                  active={selectedGrupo?.id === grupo.id}
                  className="d-flex justify-content-between align-items-center"
                  action
                  onClick={() => openGrupo(grupo)}
                >
                  <span>{grupo.nombre}</span>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(grupo);
                    }}
                  >
                    Eliminar
                  </Button>
                </ListGroup.Item>
              ))}
              {grupos.length === 0 && (
                <ListGroup.Item className="text-center text-muted">No hay grupos creados</ListGroup.Item>
              )}
            </ListGroup>
          </Col>
          <Col md={7}>
            {selectedGrupo ? (
              <>
                <h6>Miembros de "{selectedGrupo.nombre}"</h6>
                <ListGroup className="mb-3">
                  {(selectedGrupo.usuarios ?? []).map((u) => (
                    <ListGroup.Item key={u.rut} className="d-flex justify-content-between align-items-center">
                      <span>
                        {u.name} ({u.rut})
                      </span>
                      <Button size="sm" variant="outline-danger" onClick={() => handleRemoveUsuario(u.rut)}>
                        Quitar
                      </Button>
                    </ListGroup.Item>
                  ))}
                  {(selectedGrupo.usuarios ?? []).length === 0 && (
                    <ListGroup.Item className="text-center text-muted">Sin miembros</ListGroup.Item>
                  )}
                </ListGroup>
                <div className="d-flex gap-2">
                  <Form.Select value={selectedRut} onChange={(e) => setSelectedRut(e.target.value)}>
                    <option value="">Seleccione un usuario</option>
                    {users.map((u) => (
                      <option key={u.rut} value={u.rut}>
                        {u.name} ({u.rut})
                      </option>
                    ))}
                  </Form.Select>
                  <Button variant="success" onClick={handleAddUsuario}>
                    Agregar
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-muted">Seleccione un grupo para administrar sus miembros</p>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
